/**
 * CampusScene — a hand-authored line-art illustration for the login hero panel.
 *
 * The motif: a coastal campus (classical schoolhouse flanked by palm trees, a
 * rising sun, sea waves) with school-life nodes — a mortarboard and a sand
 * dollar — orbiting overhead. It nods to RISP's by-the-sea Phuket identity and
 * the sand-dollar rewards programme, drawn in the app's indigo/white palette on
 * the navy panel. Motion is a one-off staggered "rise" on load plus a few slow
 * ambient loops; all of it is disabled under `prefers-reduced-motion` (see the
 * `.campus-anim-*` keyframes in index.css).
 */
export function CampusScene({ className = '' }: { className?: string }) {
  const white = 'rgba(255,255,255,0.88)';
  const wSoft = 'rgba(255,255,255,0.34)';
  const indigo = '#8b93ec';
  const cyan = 'rgba(125,211,229,0.5)';

  return (
    <svg
      viewBox="0 0 480 380"
      fill="none"
      role="img"
      aria-label="Illustration of a seaside school campus"
      className={className}
    >
      <defs>
        <radialGradient id="cs-sun" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#c7cbff" stopOpacity="0.85" />
          <stop offset="45%" stopColor="#8b93ec" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#8b93ec" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cs-wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Sun glow + orbit rings */}
      <circle cx="240" cy="158" r="70" fill="url(#cs-sun)" className="campus-anim-pulse" />
      <g stroke={indigo} strokeOpacity="0.28" strokeDasharray="2 7" strokeLinecap="round">
        <circle cx="240" cy="158" r="96" className="campus-anim-spin-slow" style={{ transformOrigin: '240px 158px' }} />
        <circle
          cx="240"
          cy="158"
          r="128"
          className="campus-anim-spin-slower"
          style={{ transformOrigin: '240px 158px' }}
        />
      </g>

      {/* Twinkling stars */}
      <g fill={white}>
        {[
          [70, 70, 1.4, 0],
          [150, 46, 1, 0.6],
          [332, 58, 1.2, 1.1],
          [420, 104, 1, 0.3],
          [58, 150, 1, 0.9],
          [430, 190, 1.3, 1.4],
          [386, 40, 0.9, 0.2],
        ].map(([cx, cy, r, d], i) => (
          <circle key={i} cx={cx} cy={cy} r={r} className="campus-anim-twinkle" style={{ animationDelay: `${d}s` }} />
        ))}
      </g>

      {/* Orbiting node — mortarboard (top left) */}
      <g className="campus-anim-float" style={{ animationDelay: '0.2s' }}>
        <circle cx="118" cy="96" r="19" fill="#0d1330" stroke={indigo} strokeOpacity="0.55" />
        <g transform="translate(118 96)" stroke={white} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
          <path d="M-9 -1 L0 -5 L9 -1 L0 3 Z" fill={indigo} fillOpacity="0.4" />
          <path d="M-5 1 L-5 5 Q0 8 5 5 L5 1" />
          <path d="M9 -1 L9 5" />
        </g>
      </g>

      {/* Orbiting node — sand dollar (top right) */}
      <g className="campus-anim-float" style={{ animationDelay: '1.1s' }}>
        <circle cx="368" cy="112" r="20" fill="#0d1330" stroke={indigo} strokeOpacity="0.55" />
        <circle cx="368" cy="112" r="12" stroke={white} strokeWidth="1.3" />
        <g transform="translate(368 112)" stroke={white} strokeWidth="1.1" strokeLinecap="round">
          {[0, 72, 144, 216, 288].map(a => (
            <line key={a} x1="0" y1="0" x2={7 * Math.cos((a * Math.PI) / 180)} y2={7 * Math.sin((a * Math.PI) / 180)} />
          ))}
        </g>
        <circle cx="368" cy="112" r="2" fill={white} />
      </g>

      {/* Campus — rises on load */}
      <g className="campus-anim-rise" style={{ animationDelay: '0.15s' }}>
        {/* Palm trees flanking the building */}
        <g stroke={white} strokeWidth="2.2" strokeLinecap="round" fill="none">
          <g className="campus-anim-sway" style={{ transformOrigin: '116px 288px' }}>
            <path d="M116 288 C110 252 122 226 130 208" />
            <g strokeWidth="1.8">
              <path d="M130 208 C152 200 170 204 180 214" />
              <path d="M130 208 C150 188 172 184 188 190" />
              <path d="M130 208 C120 186 108 180 94 184" />
              <path d="M130 208 C110 194 92 196 82 206" />
              <path d="M130 208 C134 186 138 174 140 160" />
            </g>
          </g>
          <g className="campus-anim-sway" style={{ transformOrigin: '364px 288px', animationDelay: '0.8s' }}>
            <path d="M364 288 C370 252 358 226 350 208" />
            <g strokeWidth="1.8">
              <path d="M350 208 C328 200 310 204 300 214" />
              <path d="M350 208 C330 188 308 184 292 190" />
              <path d="M350 208 C360 186 372 180 386 184" />
              <path d="M350 208 C370 194 388 196 398 206" />
              <path d="M350 208 C346 186 342 174 340 160" />
            </g>
          </g>
        </g>

        {/* Schoolhouse */}
        <g stroke={white} strokeLinejoin="round" strokeLinecap="round">
          {/* Body */}
          <rect x="196" y="232" width="88" height="44" fill="url(#cs-wall)" strokeWidth="1.8" />
          {/* Columns */}
          <g strokeWidth="2.4" stroke={wSoft}>
            {[204, 224, 240, 256, 276].map(x => (
              <line key={x} x1={x} y1="238" x2={x} y2="272" />
            ))}
          </g>
          {/* Entablature */}
          <rect x="190" y="226" width="100" height="8" fill={indigo} fillOpacity="0.18" strokeWidth="1.8" />
          {/* Pediment */}
          <path d="M186 226 L240 194 L294 226 Z" fill="url(#cs-wall)" strokeWidth="1.8" />
          <circle cx="240" cy="216" r="4.5" strokeWidth="1.4" />
          {/* Cupola + flag */}
          <rect x="232" y="176" width="16" height="18" strokeWidth="1.6" fill="#0d1330" />
          <path d="M230 176 L240 166 L250 176 Z" strokeWidth="1.6" fill={indigo} fillOpacity="0.3" />
          <line x1="240" y1="166" x2="240" y2="148" strokeWidth="1.6" />
          <path
            d="M240 149 C247 151 249 155 256 154 L256 161 C249 162 247 158 240 160 Z"
            fill={indigo}
            fillOpacity="0.55"
            stroke={white}
            strokeWidth="1.2"
            className="campus-anim-flag"
            style={{ transformOrigin: '240px 155px' }}
          />
          {/* Door */}
          <path d="M234 276 L234 256 Q240 250 246 256 L246 276 Z" strokeWidth="1.6" fill={indigo} fillOpacity="0.25" />
          {/* Steps */}
          <g strokeWidth="1.6" stroke={wSoft}>
            <line x1="190" y1="278" x2="290" y2="278" />
            <line x1="184" y1="284" x2="296" y2="284" />
          </g>
        </g>
      </g>

      {/* Ground + drifting sea */}
      <line x1="44" y1="290" x2="436" y2="290" stroke={wSoft} strokeWidth="1.4" strokeLinecap="round" />
      <g stroke={cyan} strokeWidth="1.6" strokeLinecap="round" fill="none">
        <path
          className="campus-anim-drift"
          d="M-40 306 Q0 298 40 306 T120 306 T200 306 T280 306 T360 306 T440 306 T520 306"
        />
        <path
          className="campus-anim-drift"
          style={{ animationDelay: '1.3s', animationDuration: '7s' }}
          strokeOpacity="0.6"
          d="M-40 320 Q0 314 40 320 T120 320 T200 320 T280 320 T360 320 T440 320 T520 320"
        />
      </g>
    </svg>
  );
}
